/**
 * Local copy of the feature-gate rule-tree shape. The wire contract
 * stores this tree as `JSONObject`, so codegen has no typed view into
 * it — mops-web defines its own typed shape that matches the on-disk
 * JSONB structure produced by `@orn/core/db/schemaTypes`.
 *
 * Keep this in lockstep with `FeatureGateRuleNode` /
 * `FEATURE_GATE_RULE_TYPE` in `@orn/core/db/schemaTypes.ts`. Drift
 * surfaces immediately because the mops-api Zod schema rejects any
 * shape that is not in the four-variant discriminated union below.
 *
 * The form layer (v1) only ever emits / consumes the `is_admin`,
 * `all_users`, and `or` shapes. The `and` shape is included so reads
 * of older / hand-edited gates still type-check; the form recognizes
 * it as non-canonical and renders the read-only banner.
 */

export const FEATURE_GATE_RULE_TYPE = {
  ALL_USERS: 'all_users',
  IS_ADMIN: 'is_admin',
  AND: 'and',
  OR: 'or',
} as const;

export type FeatureGateRuleNode =
  | { type: typeof FEATURE_GATE_RULE_TYPE.ALL_USERS; rolloutPercent: number }
  | { type: typeof FEATURE_GATE_RULE_TYPE.IS_ADMIN; rolloutPercent: number }
  | { type: typeof FEATURE_GATE_RULE_TYPE.AND; children: FeatureGateRuleNode[] }
  | { type: typeof FEATURE_GATE_RULE_TYPE.OR; children: FeatureGateRuleNode[] };

/**
 * Narrows `unknown` (typically `JSONObject` off the wire) to a typed
 * `FeatureGateRuleNode`. Returns `null` for anything that does not
 * match the discriminated-union shape — the caller falls back to its
 * non-canonical banner in that case rather than crashing on a malformed
 * rule. Defense in depth: mops-api's Zod schema already rejects
 * malformed trees at the GraphQL boundary, but we still guard on read
 * because the JSONB column could in theory contain a rule type the
 * frontend predates.
 */
export function parseRuleTree(raw: unknown): FeatureGateRuleNode | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const obj = raw as { type?: unknown; rolloutPercent?: unknown; children?: unknown };
  if (obj.type === FEATURE_GATE_RULE_TYPE.ALL_USERS || obj.type === FEATURE_GATE_RULE_TYPE.IS_ADMIN) {
    if (typeof obj.rolloutPercent !== 'number') {
      return null;
    }
    return { type: obj.type, rolloutPercent: obj.rolloutPercent };
  }
  if (obj.type === FEATURE_GATE_RULE_TYPE.AND || obj.type === FEATURE_GATE_RULE_TYPE.OR) {
    if (!Array.isArray(obj.children)) {
      return null;
    }
    const children: FeatureGateRuleNode[] = [];
    for (const child of obj.children) {
      const parsed = parseRuleTree(child);
      if (!parsed) {
        return null;
      }
      children.push(parsed);
    }
    return { type: obj.type, children };
  }
  return null;
}
